import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

// Components 
import Box from '@mui/material/Box';
import Button from '../inputs/Button';
import CancelButton from '../inputs/CancelButton';
import TextField from '@mui/material/TextField';

// Redux & API
import { setNotificationMessage } from '../../../redux/features/general/notification';
import { setIsDeleteAccountPopupOpened, setIsLoadingPopupOpened } from '../../../redux/features/general/navigation';
import { confirmAccountDeletion, requestAccountDeletion } from '../../../routes/doctor/account';

interface Props { 

}; 

const DeleteAccountPopup: React.FC<Props> = ({ }) => {

    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 
    const isMobile = useMediaQuery('(max-width: 768px)');

    const colors = useAppSelector(state => state.theme.colors); 

    const [isAuthCodeSent, setIsAuthCodeSent] = useState(false); 
    const [authCode, setAuthCode] = useState(''); 
    const [deletedAccountId, setDeletedAccountId] = useState(''); 
    const [errorMessage, setErrorMessage] = useState(''); 
    const [error, setError] = useState(false); 

    // -- Handler to request the account deletion and receive a verification code -- 
    const handleAccountDeletionRequest = async () => { 
        dispatch(setIsLoadingPopupOpened(true)); 
        const res = await requestAccountDeletion(); 
        dispatch(setIsLoadingPopupOpened(false)); 

        if (res?.data.status === 200) { 
            setIsAuthCodeSent(true); 
            setDeletedAccountId(res.data.deletedAccountId); 
            dispatch(setNotificationMessage(t('authCodeSent'))); 
        }; 
    }; 

    // -- Handler to confirm the deletion with the verification code -- 
    const handleDeletionConfirmation = async () => { 
        const res = await confirmAccountDeletion(authCode, deletedAccountId) as any; 
        if (res.data.status === 200) {
            navigate('/login'); 
            window.location.reload(); 
        } else { 
            dispatch(setNotificationMessage(t('verificationCodeNotFound')));
            setErrorMessage(String(t('verificationCodeNotFound'))); 
            setError(true); 
        }; 
    }; 

    // -- Allows submitting the code by pressing Enter -- 
    const handleInput = (key: string) => { 
        if (key === 'Enter') { 
            handleDeletionConfirmation(); 
        }; 
    };

    // -- Closes the popup -- 
    const handleQuit = () => { 
        dispatch(setIsDeleteAccountPopupOpened(false)); 
    }; 
    
    // -- 

    return (

        <Box 
            onClick={handleQuit}
            sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                width: '100vw',
                height: '100vh', 
                backgroundColor: colors?.popup, 
                overflow: 'hidden', 
                position: 'absolute', 
                zIndex: 100000000,
            }}
        >

            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'space-evenly',
                    textAlign: 'center', 
                    maxWidth: isMobile ? '90vw' : '450px',
                    width: '100%',
                    minHeight: '350px',
                    padding: isMobile ? 2 : 3, 
                    borderRadius: 5,
                    fontSize: 14,
                    backgroundColor: colors?.container,
                    position: 'relative' // Needed for the CancelButton positioning
                }}
            >
                <Box
                    component="img"
                    sx={{
                        width: isMobile ? 80 : 100,
                        marginTop: 3, 
                        marginBottom: 2, 
                    }}
                    src={require('../../../assets/icons/consultation/delete-note.png')}
                />

                <Box
                    sx={{ 
                        width: '90%',
                        marginBottom: 2 
                    }}
                >{t('deleteAccountWarning')}
                </Box>

                {isAuthCodeSent && 
                    <TextField 
                        sx={{ width: isMobile ? '90%' : '80%', marginBottom: '2vh', fontSize: 10, alignSelf: 'center' }} 
                        InputProps={{
                            style: {fontSize: 16, color: colors?.text, textAlign: 'center' },
                            disableUnderline: true, 
                        }}
                        onChange={(e) => {
                            setAuthCode(e.target.value);
                            setError(false);
                            setErrorMessage('');
                        }}
                        id="standard-basic" 
                        label={t('verificationCode')} 
                        value={authCode}
                        variant="standard" 
                        error={error}
                        helperText={errorMessage}
                        autoFocus
                        onKeyDown={(e) => handleInput(e.key)}
                    />
                }

                {isAuthCodeSent ? 
                    <Button 
                        title='deleteAccount'
                        triggerFunction={handleDeletionConfirmation}
                        extraStyle={{ 
                            width: isMobile ? '90%' : '80%',
                            marginBottom: 4,
                            backgroundColor: 'rgb(150, 150, 150)',
                            '&:hover': { backgroundColor: colors?.red }
                        }}
                    /> : 
                    <Button 
                        title='sendAuthCode'
                        triggerFunction={handleAccountDeletionRequest}
                        extraStyle={{ 
                            width: isMobile ? '90%' : '80%',
                            marginBottom: 4 
                        }}
                    />
                }
                
                <CancelButton handler={handleQuit} extraStyle={{ fontSize: 30 }} />
            </Box>
        </Box>
    );
};

export default DeleteAccountPopup;
